import { useLocation, useNavigate } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { useEffect, useRef, useState } from 'react'
import '../styles/DigitalSigning.scss'
import { InfoCard2 } from '../../widgets/components/UI/InfoCard2'

import DigitalSigningCard from '../components/DigitalSigningCard'
import clsx from 'clsx'
import { KTSVG, toAbsoluteUrl } from '../../../../_metronic/helpers'
import { Filter } from '../../widgets/components/General/Filter'
import AllDocuments from '../components/AllDocuments'
import threeDots from '../../../../_metronic/assets/images/svg/investor/threeDots.svg'
import DigitalSigningTemplates from '../components/DigitalSigningTemplates'
import { useAuth } from '../../auth'
import { exportDocuments, exportTemplates, getDashboardCountsApi } from '../core/_requests'
import { useDigitalSigning } from '../core/DigitalSigningContext'
import TemplateModal from '../components/TemplateModal'
import { toast } from 'react-toastify'
import CreateDocumentModal from '../components/CreateDocumentModal'
import { PageTitle } from '../../../../_metronic/layout/core'
import { TemplateBasicDetails, TemplateDetails } from '../core/_models'
import DigitalSigningCardSkeleton from '../components/DigitalSigningCardSkeleton'
import { DigitalSigningBreadCrumbs } from '../core/_constants'
import TagManager from 'react-gtm-module'
import { GTMEvent } from '../../../hooks/gtmHelpers'
import brandingConfig from '../../../../config/brandingConfig'

function DigitalSigning() {
  const { formatMessage } = useIntl()
  const { digitalBreadCrumbs } = DigitalSigningBreadCrumbs()
  const [showKebabMenu, setShowKebabMenu] = useState(false)
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const query = searchParams.get('tabKey')
  const tab = searchParams.get('tab')
  const navigate = useNavigate()
  const [tabKey, setTabKey] = useState(Number(query) || 1)
  const [templatesSearch, setTemplatesSearch] = useState('')
  const [documentsSearch, setDocumentsSearch] = useState('')
  const { setTemplateTypesFilter, setDocumentsSubmissionFilter } = useDigitalSigning()
  const [showModal, setShowModal] = useState(false)
  const [showDocumentModal, setShowDocumentModal] = useState(false)
  const { companyId, currentUser } = useAuth()
  const [isTemplateCreateOrEdit, setIsTemplateCreateOrEdit] = useState(false)
  const [isDocumentCreate, setIsDocumentCreate] = useState(false)
  const [templateDetails, setTemplateDetails] = useState<TemplateDetails | undefined>()
  const [currentTemplateStep, setCurrentTemplateStep] = useState(1)
  const [currentDocumentStep, setCurrentDocumentStep] = useState(1)
  const [docusealId, setDocusealId] = useState<number | undefined>()
  const [isTemplateVisible, setIsTemplateVisible] = useState(true)
  const [isUpdateTemplateDropdown, setIsUpdateTemplateDropdown] = useState(false)
  const [submittersDetails, setSubmittersDetails] = useState<any>()
  const [templateId, setTemplateId] = useState<number | undefined>()
  const [dashboardLoading, setDashboardLoading] = useState(false)
  const [viewDocumentLoading, setViewDocumentLoading] = useState(false)
  const [viewTemplateLoading, setViewTemplateLoading] = useState(false)
  const [isDuplicateDocument, setIsDuplicateDocument] = useState(false)

  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const handleClickOutside = (event: any) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setShowKebabMenu(false);
    }
  };

  useEffect(() => {
    // Attach the event listener
    document.addEventListener('mousedown', handleClickOutside);

    // Cleanup the event listener on component unmount
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const {
    selectedDocumentStatusFilter,
    selectedDocumentSignatureStatusFilter,
    selectedTemplateTypeFilter,
  } = useDigitalSigning()
  const [dashboardCounts, setDashboardCounts] = useState({
    actionRequired: 0,
    completed: 0,
    waitingForOthers: 0,
    expiringSoon: 0,
    overdue: 0,
    cancelled: 0,
    totalDocuments: 0,
    totalTemplates: 0,
  })

  const getDashboardCounts = async () => {
    setDashboardLoading(true)
    try {
      const {
        data: { data },
      } = await getDashboardCountsApi(companyId!)
      if (data) {
        setDashboardCounts(data)
      }
    } catch (error) {
      console.log(error)
    } finally {
      setDashboardLoading(false)
    }
  }

  useEffect(() => {
    getDashboardCounts()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const onExportTemplates = async () => {
    try {
      const {
        data: { data: values, success, errors },
      } = await exportTemplates(companyId!)
      if (success) {
        toast.success(formatMessage({ id: 'Templates exported successfully' }))
        setShowKebabMenu(!showKebabMenu)
        const url = window.URL.createObjectURL(new Blob([values]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'templates.csv')
        document.body.appendChild(link)
        link.click()
        link.remove()
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.DigitalSigningTemplateExported,
            action: 'normal',
            metadata: {
              user_id: currentUser?.userId,
              email: currentUser?.email,
              company_id: companyId,
            },
          },
        })
      } else {
        setShowKebabMenu(!showKebabMenu)
        errors.forEach((error: string) => {
          toast.error(formatMessage({ id: error }))
        })
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.DigitalSigningTemplateExportFailed,
            action: 'normal',
            metadata: {
              user_id: currentUser?.userId,
              email: currentUser?.email,
              company_id: companyId,
              error: errors[0],
            },
          },
        })
      }
    } catch (err: any) {
      console.log(err)
    }
  }

  const onExportDocuments = async () => {
    try {
      const {
        data: { data: values, success, errors },
      } = await exportDocuments(companyId!)
      if (success) {
        toast.success(formatMessage({ id: 'Documents exported successfully' }))
        setShowKebabMenu(!showKebabMenu)
        const url = window.URL.createObjectURL(new Blob([values]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'documents.csv')
        document.body.appendChild(link)
        link.click()
        link.remove()
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.DigitalSigningDocumentExported,
            action: 'normal',
            metadata: {
              user_id: currentUser?.userId,
              email: currentUser?.email,
              company_id: companyId,
            },
          },
        })
      } else {
        setShowKebabMenu(!showKebabMenu)
        errors.forEach((error: string) => {
          toast.error(formatMessage({ id: error }))
        })
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.DigitalSigningDocumentExportFailed,
            action: 'normal',
            metadata: {
              user_id: currentUser?.userId,
              email: currentUser?.email,
              company_id: companyId,
              error: errors[0],
            },
          },
        })
      }
    } catch (err: any) {
      console.log(err)
    }
  }

  useEffect(() => {
    if (tab === 'all-documents') {
      setTabKey(1)
    } else if (tab === 'templates') {
      setTabKey(2)
    }
  }, [tab])

  const handleTabChange = (key: number, tabName: string) => {
    setTabKey(key)
    navigate(`?tab=${tabName}`, { replace: true })
  }

  return (
    <>
      <PageTitle
        breadcrumbs={digitalBreadCrumbs}
        description={formatMessage({ id: 'Digital Signing' })}
      >
        {formatMessage({ id: 'Digital Signing' })}
      </PageTitle>

      <div className='digital-signing-wrapper pt-0'>
        {dashboardLoading ? (
          <>
            <div className='d-flex justify-content-between gap-4 my-4 flex-wrap flex-md-nowrap'>
              <DigitalSigningCardSkeleton />
              <DigitalSigningCardSkeleton />
              <DigitalSigningCardSkeleton />
              <DigitalSigningCardSkeleton />
            </div>
            <div className='d-flex justify-content-between gap-4 my-4 flex-wrap flex-md-nowrap'>
              <DigitalSigningCardSkeleton />
              <DigitalSigningCardSkeleton />
              <DigitalSigningCardSkeleton />
              <DigitalSigningCardSkeleton />
            </div>
          </>
        ) : (
          <>
            <div className='d-flex justify-content-between gap-4 my-4 flex-wrap flex-md-nowrap'>
              <DigitalSigningCard
                icon='/media/icons/digital-signing/ic-action-required.svg'
                stat={dashboardCounts.actionRequired}
                title={formatMessage({ id: 'Action Required' })}
              />
              <DigitalSigningCard
                icon='/media/icons/digital-signing/ic-single-file.svg'
                stat={dashboardCounts.completed}
                title={formatMessage({ id: 'Completed' })}
              />
              <DigitalSigningCard
                icon='/media/icons/digital-signing/ic-waiting-for-others-file.svg'
                stat={dashboardCounts.waitingForOthers}
                title={formatMessage({ id: 'Waiting for Others' })}
              />
              <DigitalSigningCard
                icon='/media/icons/digital-signing/ic-expiring-soon-file.svg'
                stat={dashboardCounts.expiringSoon}
                title={formatMessage({ id: 'Expiring Soon' })}
              />
            </div>
            <div className='d-flex justify-content-between gap-4 my-4 flex-wrap flex-md-nowrap'>
              <DigitalSigningCard
                icon='/media/icons/digital-signing/ic-overdue-file.svg'
                stat={dashboardCounts.overdue}
                title={formatMessage({ id: 'Overdue' })}
              />
              <DigitalSigningCard
                icon='/media/icons/digital-signing/ic-cancelled-file.svg'
                stat={dashboardCounts.cancelled}
                title={formatMessage({ id: 'Cancelled' })}
              />
              <DigitalSigningCard
                icon='/media/icons/digital-signing/ic-drafts-file.svg'
                stat={dashboardCounts.totalDocuments}
                title={formatMessage({ id: 'Total Documents' })}
              />
              <DigitalSigningCard
                icon='/media/icons/digital-signing/ic-completed-file.svg'
                stat={dashboardCounts.totalTemplates}
                title={formatMessage({ id: 'Total Templates' })}
              />
            </div>
          </>
        )}

        <InfoCard2
          title={formatMessage({
            id: `How ${brandingConfig.brandName} Streamlines Digital Signing?`,
          })}
          desc={formatMessage({
            id: 'Easily upload new documents for digital signing, streamlining the signature process and ensuring timely execution of important agreements with blockchain-verified security and complete audit trails.',
          })}
          slug={brandingConfig.helpLinks.digitalSigningDashboardOverview}
          className='w-100'
          toolTipText={formatMessage({ id: 'Explore and learn more about Digital Signing.' })}
        />

        <div>
          <div className='d-flex h-55px border-bottom space-between w-100 justify-content-between prospective-investor-tab'>
            <ul className='nav nav-stretch nav-line-tabs nav-line-tabs-2x border-transparent fs-5  flex-nowrap mobile-tab-both'>
              <li className='nav-item mobile-tab font-size-13'>
                <div
                  className={`nav-link text-active-primary me-6 cursor-pointer ${tabKey === 1 ? 'active' : ''
                    }`}
                  onClick={() => handleTabChange(1, 'all-documents')}
                >
                  {formatMessage({ id: 'All Documents' })}
                </div>
              </li>
              <li className='nav-item mobile-tab font-size-13'>
                <div
                  className={`nav-link text-active-primary me-6 cursor-pointer ${tabKey === 2 ? 'active' : ''
                    }`}
                  onClick={() => handleTabChange(2, 'templates')}
                >
                  {formatMessage({ id: 'Templates' })}
                </div>
              </li>
            </ul>
            <div className='d-flex gap-md-2 gap-2 align-items-center flex-wrap flex-md-nowrap'>
              <div className='bg-white d-flex rounded height-36 align-items-center'>
                <button
                  className={clsx('text-clr88 btn btn-sm btn-flex fw-semibold font-size-12 height-36')}
                  id='fc-toogle'
                  title='Sort and Filter'
                  data-bs-toggle='tooltip'
                  data-bs-placement='left'
                  data-bs-dismiss='click'
                  data-bs-trigger='hover'
                  onClick={() => {
                    if (tabKey === 1) {
                      setDocumentsSubmissionFilter(true)
                    } else {
                      setTemplateTypesFilter(true)
                    }
                  }}
                >
                  <KTSVG
                    path={toAbsoluteUrl('/media/icons/duotune/general/gen031.svg')}
                    className='svg-icon-6 svg-icon-muted me-1'
                  />
                  <span className='d-none d-md-block'>{formatMessage({ id: 'Filter' })}</span>
                  {(tabKey === 1 &&
                    (selectedDocumentSignatureStatusFilter || selectedDocumentStatusFilter)) ||
                    (tabKey === 2 && selectedTemplateTypeFilter)
                    ? '*'
                    : ''}
                </button>
              </div>
              <div className='border d-flex align-items-center bg-white rounded m-0 p-0 height-36 w-mobile'>
                <KTSVG
                  path='/media/icons/duotune/general/gen021.svg'
                  className='svg-icon-3 position-absolute ms-3'
                />
                <input
                  type='text'
                  className='bg-body form-control form-control-flush ps-10 rounded-2 font-size-13 font-weight-400 p-md-5 ps-md-10 p-2 height-36'
                  name='search'
                  placeholder={formatMessage({ id: 'Search' })}
                  data-kt-search-element='input'
                  onChange={(e) => {
                    tabKey === 1
                      ? setDocumentsSearch(e.target.value)
                      : setTemplatesSearch(e.target.value)
                  }}
                  value={tabKey === 1 ? documentsSearch : templatesSearch}
                />
              </div>
              <div className='w-button-mobile'>
                <button
                  className='btn bg-primary custom-button-property font-size-13 text-white font-weight-400 height-36 d-flex justify-content-center align-items-center w-100'
                  onClick={() => {
                    if (tabKey === 1) {
                      setCurrentDocumentStep(1)
                      setDocusealId(undefined)
                      setTemplateId(undefined)
                      setCurrentTemplateStep(1)
                      setIsUpdateTemplateDropdown(false)
                      setSubmittersDetails(undefined)
                      setTemplateDetails(undefined)
                      setShowDocumentModal(true)
                      setIsDuplicateDocument(false)
                    } else {
                      setCurrentTemplateStep(1)
                      setTemplateDetails(undefined)
                      setDocusealId(undefined)
                      setIsTemplateVisible(true)
                      setTemplateId(undefined)
                      setShowModal(true)
                    }
                  }}
                >
                  {tabKey === 1
                    ? formatMessage({ id: 'New Doc' })
                    : formatMessage({ id: 'New Template' })}
                </button>
              </div>
              <div className='position-relative align-self-top'>
                <div
                  className='p-3 cursor-pointer bg-white height-36 rounded-2 d-flex justify-content-center align-items-center'
                  onClick={() => {
                    setShowKebabMenu(!showKebabMenu)
                  }}
                >
                  <img src={threeDots} alt='' width={16} height={16} />
                </div>
                {showKebabMenu && (
                  <div
                    className='card shadow rounded top-4 cursor-pointer z-index-1 position-absolute w-150px'
                    style={{ top: '40px', right: 0 }}
                    ref={dropdownRef}
                  >
                    <div className='border-gray-300 p-2 dropdown-items w-100 bg-hover-light rounded'>
                      <button
                        type='button'
                        className='m-0 border-0 bg-transparent text-dark font-size-12 bg-hover-muted'
                        onClick={() => {
                          if (tabKey === 1) {
                            onExportDocuments()
                          } else {
                            onExportTemplates()
                          }
                        }}
                      >
                        {formatMessage({ id: 'Export to CSV' })}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <Filter />
          </div>

          {/* Digital Signing Table */}
          {tabKey === 1 && (
            <AllDocuments
              documentsSearch={documentsSearch}
              setCurrentDocumentStep={setCurrentDocumentStep}
              isDocumentCreate={isDocumentCreate}
              submittersDetails={submittersDetails}
              setSubmittersDetails={setSubmittersDetails}
              setShowDocumentModal={setShowDocumentModal}
              getDocumentsDashboardCounts={getDashboardCounts}
              setIsDocumentCreate={setIsDocumentCreate}
              viewDocumentLoading={viewDocumentLoading}
              setViewDocumentLoading={setViewDocumentLoading}
              getDashboardCounts={getDashboardCounts}
              setIsDuplicateDocument={setIsDuplicateDocument}
              setDocusealId={setDocusealId}
              setTemplateId={setTemplateId}
            />
          )}

          {tabKey === 2 && (
            <DigitalSigningTemplates
              templatesSearch={templatesSearch}
              isTemplateCreateOrEdit={isTemplateCreateOrEdit}
              setIsTemplateCreateOrEdit={setIsTemplateCreateOrEdit}
              setTemplateDetails={setTemplateDetails}
              setShowModal={setShowModal}
              setCurrentTemplateStep={setCurrentTemplateStep}
              isTemplateVisible={isTemplateVisible}
              getTemplatesDashboardCounts={getDashboardCounts}
              viewTemplateLoading={viewTemplateLoading}
              setViewTemplateLoading={setViewTemplateLoading}
            />
          )}
        </div>
      </div>
      <TemplateModal
        showModal={showModal}
        setShowModal={setShowModal}
        setIsTemplateCreateOrEdit={setIsTemplateCreateOrEdit}
        templateDetails={templateDetails as unknown as TemplateBasicDetails}
        setTemplateDetails={setTemplateDetails}
        setCurrentTemplateStep={setCurrentTemplateStep}
        currentTemplateStep={currentTemplateStep}
        docusealId={docusealId}
        setDocusealId={setDocusealId}
        isTemplateVisible={isTemplateVisible}
        setIsUpdateTemplateDropdown={setIsUpdateTemplateDropdown}
        getTemplatessDashboardCounts={getDashboardCounts}
        viewTemplateLoading={viewTemplateLoading}
      />
      <CreateDocumentModal
        showModal={showDocumentModal}
        setShowModal={setShowDocumentModal}
        tabKey={tabKey}
        currentDocumentStep={currentDocumentStep}
        setCurrentDocumentStep={setCurrentDocumentStep}
        setIsDocumentCreate={setIsDocumentCreate}
        setShowTemplateModal={setShowModal}
        templateDocusealId={docusealId}
        setIsTemplateVisible={setIsTemplateVisible}
        isUpdateTemplateDropdown={isUpdateTemplateDropdown}
        submitterDetails={submittersDetails}
        getDocumentsDashboardCounts={getDashboardCounts}
        templateId={templateId}
        setTemplateId={setTemplateId}
        viewDocumentLoading={viewDocumentLoading}
        setSubmitterDetails={setSubmittersDetails}
        isDuplicateDocument={isDuplicateDocument}
      />
    </>
  )
}

export default DigitalSigning
